import React, { useState, useEffect, useCallback } from 'react';
import { useNavigate, useLocation } from 'react-router-dom';
import { useTranslation } from 'react-i18next';
import { useForm } from 'react-hook-form';
import { zodResolver } from '@hookform/resolvers/zod';
import { z } from 'zod';
import { Card, CardContent, CardHeader, CardTitle } from '@/components/ui/card';
import { Button } from '@/components/ui/button';
import { Input } from '@/components/ui/input';
import { Label } from '@/components/ui/label';
import { Alert, AlertDescription } from '@/components/ui/alert';
import { LanguageSwitcher } from '@/components/ui/language-switcher';
import { Mail, Lock, Eye, EyeOff, AlertCircle, CheckCircle, ArrowRight, Sparkles, Users, Shield } from 'lucide-react';
import { useTrialAuth } from '@/hooks/use-trial-auth';

const GOOGLE_CLIENT_ID = import.meta.env.VITE_GOOGLE_CLIENT_ID || '';

const loginSchema = z.object({
  // an email, or the short identifier a school gives its pupils
  email: z.string().trim().min(1, 'Email or username is required'),
  password: z.string().min(6, 'Password must be at least 6 characters'),
});
type LoginValues = z.infer<typeof loginSchema>;

interface LoginResponse {
  access_token?: string;
  refresh_token?: string;
  token_type?: string;
  detail?: string;
  message?: string;
}

export function LoginPageWithVerification() {
  const navigate = useNavigate();
  const location = useLocation();
  const { setTrialUser } = useTrialAuth();
  const { t } = useTranslation();
  const {
    register,
    handleSubmit: handleFormSubmit,
    watch,
    formState: { errors: formErrors },
  } = useForm<LoginValues>({
    resolver: zodResolver(loginSchema),
    defaultValues: { email: '', password: '' },
  });
  const email = watch('email');
  const password = watch('password');
  const [showPassword, setShowPassword] = useState(false);
  const [status, setStatus] = useState<'idle' | 'loading' | 'error' | 'verification_required'>('idle');
  const [message, setMessage] = useState('');
  const [canRequestVerification, setCanRequestVerification] = useState(false);
  const [googleLoaded, setGoogleLoaded] = useState(false);

  // Check for success message from email verification
  const successMessage = location.state?.message;

  // Load Google Identity Services script
  useEffect(() => {
    if (!GOOGLE_CLIENT_ID) return;
    if (document.getElementById('gsi-script')) { setGoogleLoaded(true); return; }
    const script = document.createElement('script');
    script.id = 'gsi-script';
    script.src = 'https://accounts.google.com/gsi/client';
    script.async = true;
    script.defer = true;
    script.onload = () => setGoogleLoaded(true);
    document.head.appendChild(script);
  }, []);

  const handleGoogleLogin = useCallback(() => {
    if (!GOOGLE_CLIENT_ID || !googleLoaded || !window.google) return;
    window.google.accounts.id.initialize({
      client_id: GOOGLE_CLIENT_ID,
      callback: async (response: { credential: string }) => {
        try {
          setStatus('loading');
          const { onboardingService } = await import('@/services/onboarding.service');
          // logging in never creates an account: new people sign up first,
          // where pupils give their age (parental consent)
          const authResponse = await onboardingService.googleAuth(response.credential, 'student', {
            createAccount: false,
          });

          const authUser = authResponse.user as Record<string, any>;
          const userRole = authUser?.primary_role
            || authUser?.role
            || (authUser?.roles as string[])?.[0]
            || 'student';

          // Store in trial auth store (drives isAuthenticated)
          setTrialUser({
            id: authResponse.user?.id || 'google-user',
            email: authResponse.user?.email || '',
            full_name: authResponse.user?.full_name || '',
            primary_role: userRole,
            preferred_language: 'en',
            goals: [],
            experience: 'beginner',
            trial_expires_at: new Date(Date.now() + 365 * 24 * 60 * 60 * 1000).toISOString(),
            access_token: authResponse.access_token,
            school_id: authUser?.school_id || null,
          });

          localStorage.setItem('access_token', authResponse.access_token);
          localStorage.setItem('refresh_token', authResponse.refresh_token);
          navigate('/app/dashboard');
        } catch (err) {
          const res = (err as { response?: { status?: number; data?: { detail?: string } } })?.response;
          setStatus('error');
          if (res?.status === 409) setMessage(t('loginPage.googleNoAccount'));
          else if (res?.data?.detail === 'parental_consent_pending') setMessage(t('loginPage.parentalConsentPending'));
          else setMessage(t('loginPage.googleFailed'));
        }
      },
    });
    window.google.accounts.id.prompt();
  }, [googleLoaded, setTrialUser]);

  const handleSubmit = async ({ email: loginEmail, password: loginPassword }: LoginValues) => {
    setStatus('loading');
    setMessage('');

    const API_URL = import.meta.env.VITE_API_BASE_URL || import.meta.env.VITE_API_URL || 'http://localhost:8000/api/v1';

    try {
      const formData = new FormData();
      formData.append('username', loginEmail); // Backend expects 'username' field
      formData.append('password', loginPassword);

      const response = await fetch(`${API_URL}/auth/login`, {
        method: 'POST',
        body: formData,
      });

      const data: LoginResponse = await response.json();

      if (response.ok) {
        // Login successful - store tokens using trial auth system
        // Fetch user profile to get additional details
        const userResponse = await fetch(`${API_URL}/auth/me`, {
          headers: {
            'Authorization': `Bearer ${data.access_token}`
          }
        });

        let userData: Record<string, unknown> = {};
        if (userResponse.ok) {
          userData = await userResponse.json();
        }

        // Store in trial auth (which works for local dev mode)
        setTrialUser({
          id: (userData.id as string) || 'temp-id',
          email: (userData.email as string) || email,
          full_name: (userData.full_name as string) || email.split('@')[0],
          primary_role: ((userData.roles as Array<string | {name: string}>)?.[0] as any)?.name || ((userData.roles as string[])?.[0]) || 'student',
          preferred_language: (userData.preferred_language as string) || 'en',
          goals: [],
          experience: 'beginner',
          trial_expires_at: new Date(Date.now() + 365 * 24 * 60 * 60 * 1000).toISOString(), // 1 year
          access_token: data.access_token || '',
          school_id: (userData.school_id as string) || null,
        });

        // Also store in localStorage for backward compatibility
        localStorage.setItem('access_token', data.access_token || '');
        if (data.refresh_token) {
          localStorage.setItem('refresh_token', data.refresh_token);
        }

        navigate('/app/dashboard', { replace: true });
      } else if (response.status === 403) {
        // Email verification required
        const isVerificationError = response.headers.get('X-Verification-Required') === 'true';

        if (isVerificationError) {
          setStatus('verification_required');
          setCanRequestVerification(true);
          setMessage(data.detail || t('loginPage.verificationRequired'));
        } else if (data.detail === 'parental_consent_pending') {
          setStatus('verification_required');
          setMessage(t('loginPage.parentalConsentPending'));
        } else {
          setStatus('error');
          setMessage(data.detail || t('loginPage.loginFailed'));
        }
      } else if (response.status === 429) {
        setStatus('error');
        setMessage(t('loginPage.tooManyAttempts', 'Too many wrong passwords for this account. Wait 15 minutes, or use "Forgot password?".'));
      } else {
        setStatus('error');
        setMessage(data.detail || t('loginPage.loginFailed'));
      }
    } catch (error) {
      setStatus('error');
      setMessage(t('loginPage.networkError'));
    }
  };

  const handleRequestVerification = async () => {
    try {
      setStatus('loading');

      const formData = new FormData();
      formData.append('username', email);
      formData.append('password', password);

      const API_URL = import.meta.env.VITE_API_BASE_URL || import.meta.env.VITE_API_URL || 'http://localhost:8000/api/v1';
      const response = await fetch(`${API_URL}/auth/request-verification`, {
        method: 'POST',
        body: formData,
      });

      const data = await response.json();

      if (response.ok) {
        setMessage(`✅ ${data.message}`);
        setCanRequestVerification(false);
        setStatus('idle');
      } else {
        setStatus('error');
        setMessage(data.detail || t('loginPage.verificationEmailFailed', 'Failed to send verification email.'));
      }
    } catch (error) {
      setStatus('error');
      setMessage(t('loginPage.networkError'));
    }
  };

  return (
    <div className="min-h-screen bg-[#F6F8FB] grid lg:grid-cols-2 relative">
      {/* Hero panel — left */}
      <aside className="relative hidden overflow-hidden bg-[#13225B] text-white lg:flex lg:flex-col lg:justify-between lg:px-12 lg:py-10">
        <div className="pointer-events-none absolute -right-32 top-1/3 h-[520px] w-[520px] rounded-full bg-gradient-to-br from-[#0B4DBA]/40 to-[#FF7A00]/30 blur-[120px]" />
        <div className="pointer-events-none absolute inset-0 bg-[radial-gradient(circle_at_top_right,rgba(255,255,255,0.08),transparent_60%)]" />
        <div className="relative flex items-center gap-3">
          <img src="/logo-white.svg" alt="AppAcademia" className="h-9 w-9" />
          <span className="font-display text-xl font-bold">{t('common.appName')}</span>
        </div>
        <div className="relative max-w-md space-y-6">
          <h2 className="font-display text-4xl font-bold leading-tight tracking-tight xl:text-5xl">
            {t('loginPage.heroHeadline', 'Your whole school, intelligently connected.')}
          </h2>
          <p className="text-base text-white/75">
            {t('loginPage.heroSubtitle', 'AI tutoring, exam prep, attendance, payments and parent communication — one secure platform for every role.')}
          </p>
          <dl className="grid max-w-sm grid-cols-3 gap-6 pt-2">
            <div>
              <dt className="font-display text-3xl font-bold">200+</dt>
              <dd className="text-xs uppercase tracking-wide text-white/60">{t('loginPage.heroFeatures', 'Features')}</dd>
            </div>
            <div>
              <dt className="font-display text-3xl font-bold">8</dt>
              <dd className="text-xs uppercase tracking-wide text-white/60">{t('loginPage.heroRoles', 'Roles')}</dd>
            </div>
            <div>
              <dt className="font-display text-3xl font-bold">99.9%</dt>
              <dd className="text-xs uppercase tracking-wide text-white/60">{t('loginPage.heroUptime', 'Uptime')}</dd>
            </div>
          </dl>
        </div>
        <div className="relative flex items-center gap-2 text-xs text-white/60">
          <Shield className="h-3.5 w-3.5" />
          <span>{t('loginPage.heroCompliance', 'GDPR · FERPA · COPPA · WCAG 2.2 AA compliant')}</span>
        </div>
      </aside>

      {/* Right column — form */}
      <main className="relative flex items-center justify-center p-6 lg:p-10">
        <div className="absolute right-4 top-4 z-10">
          <LanguageSwitcher />
        </div>

        <div className="w-full max-w-md space-y-4">
        <Card className="rounded-2xl border border-[#E7ECF2] bg-white shadow-[0_4px_12px_rgba(15,23,42,.07)]">
          <CardHeader className="space-y-2 pb-6 pt-8 text-left">
            <div className="flex items-center gap-3 lg:hidden">
              <div className="flex h-12 w-12 items-center justify-center rounded-2xl bg-gradient-to-br from-[#0B4DBA] to-[#FF7A00] shadow-md">
                <img src="/logo-white.svg" alt="AppAcademia" className="h-7 w-7" />
              </div>
              <span className="font-display text-lg font-bold text-[#13225B]">{t('common.appName')}</span>
            </div>
            <CardTitle className="font-display text-3xl font-bold text-[#13225B]">
              {t('loginPage.welcomeBack', 'Welcome back')}
            </CardTitle>
            <p className="text-sm text-slate-500">
              {t('loginPage.subtitle')}
            </p>
          </CardHeader>

          <CardContent className="space-y-5">
            {successMessage && (
              <Alert className="border-green-200 bg-gradient-to-r from-green-50 to-emerald-50 animate-in slide-in-from-top">
                <CheckCircle className="h-4 w-4 text-green-600" />
                <AlertDescription className="text-green-800 font-medium">{successMessage}</AlertDescription>
              </Alert>
            )}

            <form onSubmit={handleFormSubmit(handleSubmit)} className="space-y-4">
              <div className="space-y-2">
                <Label htmlFor="email" className="text-gray-700 font-semibold">{t('loginPage.emailLabel')}</Label>
                <div className="relative group">
                  <Mail className="absolute left-3 top-1/2 -translate-y-1/2 h-5 w-5 text-gray-400 group-focus-within:text-primary-500 transition-colors" />
                  <Input id="email" type="text" autoCapitalize="none" autoComplete="username" {...register('email')} placeholder={t('loginPage.emailPlaceholder')} className={`pl-11 h-12 border-gray-200 focus:border-primary-500 focus:ring-primary-500/20 transition-all ${formErrors.email ? 'border-red-400 focus:border-red-500' : ''}`} />
                </div>
                {formErrors.email && (
                  <p className="text-sm text-red-600 mt-1">{t(`loginPage.errors.${formErrors.email.type}Email`, formErrors.email.message)}</p>
                )}
              </div>

              <div className="space-y-2">
                <Label htmlFor="password" className="text-gray-700 font-semibold">{t('loginPage.passwordLabel')}</Label>
                <div className="relative group">
                  <Lock className="absolute left-3 top-1/2 -translate-y-1/2 h-5 w-5 text-gray-400 group-focus-within:text-primary-500 transition-colors" />
                  <Input id="password" type={showPassword ? 'text' : 'password'} autoComplete="current-password" {...register('password')} placeholder={t('loginPage.passwordPlaceholder')} className={`pl-11 pr-11 h-12 border-gray-200 focus:border-primary-500 focus:ring-primary-500/20 transition-all ${formErrors.password ? 'border-red-400 focus:border-red-500' : ''}`} />
                  <button type="button" onClick={() => setShowPassword(!showPassword)} className="absolute right-3 top-1/2 -translate-y-1/2 text-gray-400 hover:text-gray-600 transition-colors">
                    {showPassword ? <EyeOff className="h-5 w-5" /> : <Eye className="h-5 w-5" />}
                  </button>
                </div>
                {formErrors.password && (
                  <p className="text-sm text-red-600 mt-1">{t(`loginPage.errors.${formErrors.password.type}Password`, formErrors.password.message)}</p>
                )}
              </div>

              {message && (
                <Alert className={`border-2 animate-in slide-in-from-top ${
                  status === 'verification_required' ? 'border-orange-200 bg-gradient-to-r from-orange-50 to-amber-50'
                  : status === 'error' ? 'border-red-200 bg-gradient-to-r from-red-50 to-pink-50'
                  : 'border-green-200 bg-gradient-to-r from-green-50 to-emerald-50'
                }`}>
                  <AlertCircle className={`h-5 w-5 ${
                    status === 'verification_required' ? 'text-orange-600' : status === 'error' ? 'text-red-600' : 'text-green-600'
                  }`} />
                  <AlertDescription className={`font-medium ${
                    status === 'verification_required' ? 'text-orange-800' : status === 'error' ? 'text-red-800' : 'text-green-800'
                  }`}>{message}</AlertDescription>
                </Alert>
              )}

              {status === 'verification_required' && canRequestVerification && (
                <Button type="button" onClick={handleRequestVerification} variant="outline" className="w-full h-12 border-2 border-orange-300 text-orange-700 hover:bg-orange-50 hover:border-orange-400 font-semibold transition-all" disabled={status as string === 'loading'}>
                  <Mail className="mr-2 h-5 w-5" />
                  {t('loginPage.sendVerification')}
                </Button>
              )}

              <Button type="submit" className="h-12 w-full rounded-xl bg-gradient-to-r from-[#0B4DBA] to-[#FF7A00] text-base font-semibold text-white shadow-[0_8px_24px_rgba(11,77,186,.22)] transition-all hover:from-[#0941A0] hover:to-[#E66D00] hover:shadow-[0_10px_28px_rgba(11,77,186,.28)] active:scale-[0.98]" disabled={status as string === 'loading'}>
                {status === 'loading' ? (
                  <span className="flex items-center"><Sparkles className="mr-2 h-5 w-5 animate-spin" />{t('loginPage.signingIn')}</span>
                ) : (
                  <span className="flex items-center">{t('loginPage.signIn')}<ArrowRight className="ml-2 h-5 w-5" /></span>
                )}
              </Button>

              <div className="text-right">
                <button type="button" onClick={() => navigate('/auth/forgot-password')} className="text-sm text-primary-600 hover:text-primary-700 font-medium hover:underline transition-colors">
                  {t('loginPage.forgotPassword', 'Forgot your password?')}
                </button>
              </div>
            </form>

            {/* Google Sign In */}
            {GOOGLE_CLIENT_ID && (
              <>
                <div className="relative">
                  <div className="absolute inset-0 flex items-center"><div className="w-full border-t border-gray-200"></div></div>
                  <div className="relative flex justify-center text-sm"><span className="px-4 bg-white text-gray-500 font-medium">{t('loginPage.or')}</span></div>
                </div>
                <button type="button" onClick={handleGoogleLogin} className="w-full h-12 flex items-center justify-center gap-3 border-2 border-gray-200 rounded-xl text-gray-700 font-semibold hover:bg-gray-50 hover:border-gray-300 transition-all">
                  <svg width="20" height="20" viewBox="0 0 24 24"><path d="M22.56 12.25c0-.78-.07-1.53-.2-2.25H12v4.26h5.92a5.06 5.06 0 01-2.2 3.32v2.77h3.57c2.08-1.92 3.28-4.74 3.28-8.1z" fill="#4285F4"/><path d="M12 23c2.97 0 5.46-.98 7.28-2.66l-3.57-2.77c-.98.66-2.23 1.06-3.71 1.06-2.86 0-5.29-1.93-6.16-4.53H2.18v2.84C3.99 20.53 7.7 23 12 23z" fill="#34A853"/><path d="M5.84 14.09c-.22-.66-.35-1.36-.35-2.09s.13-1.43.35-2.09V7.07H2.18C1.43 8.55 1 10.22 1 12s.43 3.45 1.18 4.93l2.85-2.22.81-.62z" fill="#FBBC05"/><path d="M12 5.38c1.62 0 3.06.56 4.21 1.64l3.15-3.15C17.45 2.09 14.97 1 12 1 7.7 1 3.99 3.47 2.18 7.07l3.66 2.84c.87-2.6 3.3-4.53 6.16-4.53z" fill="#EA4335"/></svg>
                  {t('loginPage.continueWithGoogle')}
                </button>
              </>
            )}

            {/* Verification link */}
            <div className="text-center">
              <button onClick={() => navigate('/auth/resend-verification')} className="text-sm text-gray-500 hover:text-primary-600 font-medium hover:underline transition-colors inline-flex items-center">
                <Mail className="mr-1.5 h-3.5 w-3.5" />
                {t('loginPage.needVerification')}
              </button>
            </div>

            <div className="pt-3 border-t border-gray-100">
              <div className="flex items-center justify-center space-x-2 text-xs text-gray-400">
                <Lock className="h-3.5 w-3.5" />
                <span>{t('loginPage.dataSecure')}</span>
              </div>
            </div>
          </CardContent>
        </Card>

        {/* New to AppAcademia — clear persona paths */}
        <Card className="border-0 shadow-lg bg-white/95 backdrop-blur-sm">
          <CardContent className="p-5">
            <p className="text-center text-sm font-semibold text-gray-700 mb-4">{t('loginPage.newToApp')}</p>
            <div className="space-y-2.5">
              {/* School Admin */}
              <button onClick={() => navigate('/onboard/school-admin')} className="w-full flex items-center gap-3 p-3 rounded-xl border border-gray-100 hover:border-primary-200 hover:bg-primary-50/50 transition-all group text-left">
                <div className="p-2 rounded-lg bg-blue-100 text-blue-600 group-hover:bg-blue-200 transition-colors">
                  <Shield className="h-5 w-5" />
                </div>
                <div className="flex-1 min-w-0">
                  <p className="text-sm font-semibold text-gray-800">{t('loginPage.schoolAdmin')}</p>
                  <p className="text-xs text-gray-500">{t('loginPage.schoolAdminDesc')}</p>
                </div>
                <ArrowRight className="h-4 w-4 text-gray-300 group-hover:text-primary-500 transition-colors flex-shrink-0" />
              </button>

              {/* Teacher or Parent */}
              <button onClick={() => navigate('/onboard/individual')} className="w-full flex items-center gap-3 p-3 rounded-xl border border-gray-100 hover:border-primary-200 hover:bg-primary-50/50 transition-all group text-left">
                <div className="p-2 rounded-lg bg-green-100 text-green-600 group-hover:bg-green-200 transition-colors">
                  <Users className="h-5 w-5" />
                </div>
                <div className="flex-1 min-w-0">
                  <p className="text-sm font-semibold text-gray-800">{t('loginPage.teacherOrParent')}</p>
                  <p className="text-xs text-gray-500">{t('loginPage.teacherOrParentDesc')}</p>
                </div>
                <ArrowRight className="h-4 w-4 text-gray-300 group-hover:text-primary-500 transition-colors flex-shrink-0" />
              </button>

              {/* Student */}
              <button onClick={() => navigate('/onboard/quick-student')} className="w-full flex items-center gap-3 p-3 rounded-xl border border-gray-100 hover:border-primary-200 hover:bg-primary-50/50 transition-all group text-left">
                <div className="p-2 rounded-lg bg-secondary-100 text-secondary-600 group-hover:bg-secondary-200 transition-colors">
                  <Sparkles className="h-5 w-5" />
                </div>
                <div className="flex-1 min-w-0">
                  <p className="text-sm font-semibold text-gray-800">{t('loginPage.student')}</p>
                  <p className="text-xs text-gray-500">{t('loginPage.studentDesc')}</p>
                </div>
                <ArrowRight className="h-4 w-4 text-gray-300 group-hover:text-primary-500 transition-colors flex-shrink-0" />
              </button>
            </div>

            {/* Join with school code */}
            <div className="mt-4 pt-3 border-t border-gray-100 text-center">
              <p className="text-xs text-gray-500 mb-1">{t('loginPage.haveJoinCode')}</p>
              <button onClick={() => navigate('/onboard/individual?joinCode=true')} className="text-sm font-semibold text-primary-600 hover:text-primary-700 hover:underline transition-colors inline-flex items-center gap-1">
                {t('loginPage.joinWithCode')}
                <ArrowRight className="h-3.5 w-3.5" />
              </button>
            </div>
          </CardContent>
        </Card>

          {import.meta.env.VITE_DISABLE_SSO === 'true' && (
            <div className="absolute bottom-4 right-4 rounded-lg border border-yellow-300 bg-yellow-100 px-3 py-2 text-xs font-medium text-yellow-800 shadow-md">
              🔧 Local Dev Mode
            </div>
          )}
        </div>
      </main>
    </div>
  );
}
